"use client";

import { HmcNavBar, HmcNavLink } from "@adi-ctx/harmonic-core-components-react";
import {
	Logo,
	type TDropdownMenuItem,
	withDropdownMenu,
	SearchDrawer,
} from "@/components";
import t from "@/utils/translate.utils";
import { usePathname } from "next/navigation";
import { pathNames } from "@/lib/constants/pathNames.constants";
import { AppProgressBar as ProgressBar, useRouter } from "next-nprogress-bar";
import { useEffect, useState } from "react";
import RightActions from "./right-actions";

type TNavItem = {
	id: string;
	dropdownItems: TDropdownMenuItem[];
};

const navItems: TNavItem[] = [
	{
		id: "connect",
		dropdownItems: [
			{
				id: "events",
				label: t("header.nav-events"),
				path: pathNames.events,
			},
			{
				id: "open-source",
				label: t("header.nav-open-source"),
				path: pathNames.openSource,
			},
			{
				id: "newsroom",
				label: t("header.nav-newsroom"),
				path: pathNames.newsroom,
			},
		],
	},
	{
		id: "learn",
		dropdownItems: [
			{
				id: "solutions",
				label: t("header.nav-solutions"),
				path: pathNames.solutions,
			},
			{
				id: "documentation",
				label: t("header.nav-documentation"),
				path: pathNames.documentation,
			},
		],
	},
	{
		id: "build",
		dropdownItems: [
			{
				id: "drivers",
				label: t("header.nav-drivers"),
				path: pathNames.drivers,
			},
		],
	},
];

export default function Header() {
	const router = useRouter();

	const [isOpen, setIsOpen] = useState(false);
	const pathName = usePathname();
	const NavLink = withDropdownMenu(HmcNavLink);

	useEffect(() => {
		const handleKeyDown = (e: any) => {
			if (e.key === "Escape") {
				setIsOpen(false);
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	useEffect(() => {
		const htmlTag = document.getElementsByTagName("html")[0];

		if (isOpen) {
			htmlTag.classList.add("no-scroll");
			htmlTag.classList.remove("overflow-y-scroll");
		} else {
			htmlTag.classList.remove("no-scroll");
			htmlTag.classList.add("overflow-y-scroll");
		}
	}, [isOpen]);

	return (
		<>
			<ProgressBar
				shallowRouting
				height="2px"
				color="#98bdf0"
				options={{ showSpinner: false }}
			/>

			<header data-testid="header">
				<div className="bg-layoutContainer text-systemInfo">
					<HmcNavBar
						aria-label={t("header.nav-aria-label")}
						data-testid="header-nav"
						className="bg-layoutContainer"
					>
						<div slot="logo" className="flex flex-row border-black">
							<Logo placement="header" data-testid="header-nav-logo" />
						</div>

						{navItems.map((item) => (
							<NavLink
								key={item.id}
								menuItems={item.dropdownItems}
								size="md"
								role="listitem"
								orientation="left"
								data-testid={`header-nav-${item.id}`}
								linkActive={item.dropdownItems
									.map((item) => item.path)
									.some((path) => pathName.includes(path!))}
								onLinkClick={(e: any) => e.preventDefault()}
								onSelection={(item: TDropdownMenuItem) => {
									router.push(item.path!);
								}}
							>
								<span className="text-systemInfo md:px-2">
									{t(`header.nav-${item.id}`)}
								</span>
							</NavLink>
						))}
						<div slot="right-actions">
							<RightActions onToggleOpen={setIsOpen} isOpen={isOpen} />
						</div>
					</HmcNavBar>

					{isOpen && <SearchDrawer setIsOpenCb={setIsOpen} />}
				</div>
			</header>
		</>
	);
}
